import { useState } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  CheckboxWithLabel,
  DismissibleTag,
  Button,
  SearchInput,
  cn,
} from "@jem-hr/jem-ui";
import { ChevronDown } from "lucide-react";

export interface BranchFilterProps {
  branches: string[];
  selectedBranches?: string[];
  onApply?: (selected: string[]) => void;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
}

export function BranchFilter({
  branches,
  selectedBranches: controlledSelected,
  onApply,
  label = "Filter by branch",
  placeholder = "Select branches...",
  searchPlaceholder = "Search...",
  className,
}: BranchFilterProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(
    controlledSelected ?? ["All Branches"]
  );
  const [pendingSelected, setPendingSelected] = useState<string[]>(selected);
  const [search, setSearch] = useState("");

  const filteredBranches = branches.filter((b) =>
    b.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setPendingSelected(selected);
      setSearch("");
    }
    setOpen(isOpen);
  };

  const handleToggle = (branch: string, checked: boolean) => {
    if (checked) {
      setPendingSelected((prev) => [...prev, branch]);
    } else {
      setPendingSelected((prev) => prev.filter((b) => b !== branch));
    }
  };

  const handleApply = () => {
    setSelected(pendingSelected);
    onApply?.(pendingSelected);
    setOpen(false);
  };

  const handleDismiss = (branch: string) => {
    const updated = selected.filter((b) => b !== branch);
    setSelected(updated);
    onApply?.(updated);
  };

  return (
    <div className={cn("max-w-[382px]", className)}>
      {label && (
        <label className="text-sm font-semibold leading-18 text-greyscale-text-title mb-xxxs block">
          {label}
        </label>
      )}

      <Popover open={open} onOpenChange={handleOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-xxs w-full min-h-[46px] rounded-lg border border-greyscale-border bg-white px-sm py-xxs text-left"
          >
            <div className="flex flex-wrap gap-xxs flex-1">
              {selected.length > 0 ? (
                selected.map((branch) => (
                  <DismissibleTag
                    key={branch}
                    variant="pink"
                    onDismiss={() => handleDismiss(branch)}
                  >
                    {branch}
                  </DismissibleTag>
                ))
              ) : (
                <span className="text-sm text-primary-navy-300">
                  {placeholder}
                </span>
              )}
            </div>
            <ChevronDown size={16} className="text-greyscale-text-caption shrink-0" />
          </button>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-[382px] p-none">
          <div className="p-xs">
            <SearchInput
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch("")}
            />
          </div>

          <div className="max-h-[200px] overflow-auto px-xs">
            {filteredBranches.map((branch) => (
              <div key={branch} className="py-xxs">
                <CheckboxWithLabel
                  label={branch}
                  checked={pendingSelected.includes(branch)}
                  onCheckedChange={(checked) =>
                    handleToggle(branch, checked as boolean)
                  }
                />
              </div>
            ))}
            {filteredBranches.length === 0 && (
              <p className="text-sm text-greyscale-text-caption py-xxs">
                No branches found.
              </p>
            )}
          </div>

          <div className="p-xs">
            <Button variant="outline" onClick={handleApply} className="w-full">
              Apply Filters
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
